export interface ConfigAPI {
  uuid: string;
  externalId?: string;
  configMap: Map<string, string>;
}

// 1. TypeScript interface matching exact JSON structure
export interface ColumnConfig {
  key: string;
  formFieldType: number;
  name: string;
  sort: number;
  width: number;
  visible: boolean;
}

export interface WarehouseReleaseTableConfig {
  itemKey: ColumnConfig;
  quantity: ColumnConfig;
  name: ColumnConfig;
  unit: ColumnConfig;
  description: ColumnConfig;
}

// Function to map API string to warehouseReleaseTableConfig

export function parseWarehouseReleaseTableConfig(
  apiStringValue: string
): WarehouseReleaseTableConfig {
  const jsonString = apiStringValue
    .replace(/^"warehouse_release_table":/, '')
    .replace(/^"/, '')
    .replace(/"$/, '')
    .replace(/'/g, '"');

  const rawConfig: Partial<Record<keyof WarehouseReleaseTableConfig, Partial<ColumnConfig>>> =
    JSON.parse(jsonString);

  const defaults: WarehouseReleaseTableConfig = {
    itemKey: {
      key: 'itemKey',
      formFieldType: 3,
      name: 'Index',
      sort: 2,
      width: 2,
      visible: true,
    },
    quantity: {
      key: 'quantity',
      formFieldType: 5,
      name: 'Ilość',
      sort: 3,
      width: 1,
      visible: true,
    },
    name: {
      key: 'name',
      formFieldType: 3,
      name: 'Nazwa',
      sort: 1,
      width: 5,
      visible: true,
    },
    unit: {
      key: 'unit',
      formFieldType: 3,
      name: 'J.m.',
      sort: 4,
      width: 1,
      visible: true,
    },
    description: {
      key: 'description',
      formFieldType: 3,
      name: 'Description/Opis',
      sort: 5,
      width: -1,
      visible: false,
    },
  };

  return {
    itemKey: {
      key: rawConfig.itemKey?.key ?? defaults.itemKey.key,
      formFieldType: rawConfig.itemKey?.formFieldType ?? defaults.itemKey.formFieldType,
      name: rawConfig.itemKey?.name ?? defaults.itemKey.name,
      sort: rawConfig.itemKey?.sort ?? defaults.itemKey.sort,
      width: rawConfig.itemKey?.width ?? defaults.itemKey.width,
      visible: rawConfig.itemKey?.visible ?? defaults.itemKey.visible,
    },
    quantity: {
      key: rawConfig.quantity?.key ?? defaults.quantity.key,
      formFieldType: rawConfig.quantity?.formFieldType ?? defaults.quantity.formFieldType,
      name: rawConfig.quantity?.name ?? defaults.quantity.name,
      sort: rawConfig.quantity?.sort ?? defaults.quantity.sort,
      width: rawConfig.quantity?.width ?? defaults.quantity.width,
      visible: rawConfig.quantity?.visible ?? defaults.quantity.visible,
    },
    name: {
      key: rawConfig.name?.key ?? defaults.name.key,
      formFieldType: rawConfig.name?.formFieldType ?? defaults.name.formFieldType,
      name: rawConfig.name?.name ?? defaults.name.name,
      sort: rawConfig.name?.sort ?? defaults.name.sort,
      width: rawConfig.name?.width ?? defaults.name.width,
      visible: rawConfig.name?.visible ?? defaults.name.visible,
    },
    unit: {
      key: rawConfig.unit?.key ?? defaults.unit.key,
      formFieldType: rawConfig.unit?.formFieldType ?? defaults.unit.formFieldType,
      name: rawConfig.unit?.name ?? defaults.unit.name,
      sort: rawConfig.unit?.sort ?? defaults.unit.sort,
      width: rawConfig.unit?.width ?? defaults.unit.width,
      visible: rawConfig.unit?.visible ?? defaults.unit.visible,
    },
    description: {
      key: rawConfig.description?.key ?? defaults.description.key,
      formFieldType: rawConfig.description?.formFieldType ?? defaults.description.formFieldType,
      name: rawConfig.description?.name ?? defaults.description.name,
      sort: rawConfig.description?.sort ?? defaults.description.sort,
      width: rawConfig.description?.width ?? defaults.description.width,
      visible: rawConfig.description?.visible ?? defaults.description.visible,
    },
  };
}

// 2. Class holding the JSON values exactly as structure requires
export class WarehouseReleaseTableConfigData {
  public warehouseReleaseTableConfig: WarehouseReleaseTableConfig;

  constructor(apiData: WarehouseReleaseTableConfig) {
    this.warehouseReleaseTableConfig = {
      itemKey: {
        key: apiData.itemKey.key,        // "itemKey"
        formFieldType: apiData.itemKey.formFieldType,  // 3
        name: apiData.itemKey.name,      // "Index"
        sort: apiData.itemKey.sort,      // 2
        width: apiData.itemKey.width,    // 2
        visible: apiData.itemKey.visible // true
      },
      quantity: {
        key: apiData.quantity.key,        // "quantity"
        formFieldType: apiData.quantity.formFieldType,  // 5
        name: apiData.quantity.name,      // "Ilość"
        sort: apiData.quantity.sort,      // 3
        width: apiData.quantity.width,    // 1
        visible: apiData.quantity.visible // true
      },
      name: {
        key: apiData.name.key,            // "name"
        formFieldType: apiData.name.formFieldType,    // 3
        name: apiData.name.name,          // "Nazwa"
        sort: apiData.name.sort,          // 1
        width: apiData.name.width,        // 5
        visible: apiData.name.visible     // true
      },
      unit: {
        key: apiData.unit.key,            // "unit"
        formFieldType: apiData.unit.formFieldType,    // 3
        name: apiData.unit.name,          // "J.m."
        sort: apiData.unit.sort,          // 4
        width: apiData.unit.width,        // 1
        visible: apiData.unit.visible     // true
      },
      description: {
        key: apiData.description.key,            // "description"
        formFieldType: apiData.description.formFieldType,    // 3
        name: apiData.description.name,          // "J.m."
        sort: apiData.description.sort,          // 4
        width: apiData.description.width,        // -1
        visible: apiData.description.visible     // false
      },
    };
  }
}


/**
 * Converts WarehouseReleaseTableConfig back to API string format
 * Returns: "warehouse_release_table":"{'itemKey':{...},'quantity':{...},...}"
 */
export function serializeWarehouseReleaseTableConfig(config: WarehouseReleaseTableConfig): string {
  // Convert to raw object with single quotes (matching API format)
  const rawConfig = {
    itemKey: {
      key: config.itemKey.key,
      formFieldType: config.itemKey.formFieldType,
      name: config.itemKey.name,
      sort: config.itemKey.sort,
      width: config.itemKey.width,
      visible: config.itemKey.visible
    },
    quantity: {
      key: config.quantity.key,
      formFieldType: config.quantity.formFieldType,
      name: config.quantity.name,
      sort: config.quantity.sort,
      width: config.quantity.width,
      visible: config.quantity.visible
    },
    name: {
      key: config.name.key,
      formFieldType: config.name.formFieldType,
      name: config.name.name,
      sort: config.name.sort,
      width: config.name.width,
      visible: config.name.visible
    },
    unit: {
      key: config.unit.key,
      formFieldType: config.unit.formFieldType,
      name: config.unit.name,
      sort: config.unit.sort,
      width: config.unit.width,
      visible: config.unit.visible
    },
    description: {
      key: config.description.key,
      formFieldType: config.description.formFieldType,
      name: config.description.name,
      sort: config.description.sort,
      width: config.description.width,
      visible: config.description.visible
    }
  };

  // Stringify with single quotes to match exact API format
  const jsonString = JSON.stringify(rawConfig)
    .replace(/"/g, "'");  // Double quotes → single quotes

  // Wrap in exact API format
  // return `"warehouse_release_table":"${jsonString}"`;
  return jsonString;
}


export class Configuration {
  warning: number;
  alert: number;
  orderNumberDateFrom: string;
  menuHideRequests: boolean;
  menuHideAgreements: boolean;
  menuHideWorkTime: boolean;
  menuHideWarehouses: boolean;
  menuHideProducts: boolean;
  allowOrderWoDevices: boolean;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  enableUserSchedule: boolean;
  teamLeadOn: boolean;
  statusChangeWorkOff: boolean;
  noteAddWorkOff: boolean;
  photoAddWorkOff: boolean;
  allowPhotoFromGallery: boolean;
  allowAddingPdfToOrder: boolean;
  formAllowSaveAsDraft: boolean;
  formAllowCopyFromPDF: boolean;
  formTableMaxCol: number;
  formTableMaxRow: number;
  formFieldFontFactor: number;
  formTableFontFactor: number;
  formEmailAddress: string;
  formCopyEmailAddress: string;
  formEmailTemplateId: string;
  warehouseReleaseTableConfigData?: WarehouseReleaseTableConfigData;
  warehouseEnableStockOrder: boolean;
  warehouseAllowNegativeStock: boolean;
  warehouseAllowAdditionalSrvm: boolean;
  warehouseNotShopZeroStock: boolean;
  warehouseAllowedStatuses: string[];
  branchesEnable: boolean;
  branchesBranchChangeByBranchAdmin: boolean;
  branchesRemoveTeamFromOrder: boolean;
  departmentsEnable: boolean;
  sequentialActionsEnable: boolean;
  customerAllowDuplicatedVATcode: boolean;
  enableSupervisorRole: boolean;
  trackingEnable: boolean;
  trackingInBackground: boolean;
  trackingHistoryDays: number;
  trackingCalculateDistance: boolean;
  trackingDistanceHistoryDays: number;
  securityEnableMFA: boolean;
  securityMFASMS:boolean;
  securityMFAAuthApp: boolean;
  securityMFAPhoneLocalBiometric: boolean;
  securityLoginLocalBiometric:boolean;


  setDefault() {
    const map = new Map<string, string>();
    this.fromMap(map);
  }

  setConfig(mp: Map<string, string>) {
    this.fromMap(mp);
  }

  fromMap(map: Map<string, string>) {
    this.warning = map.get('warning') ? +map.get('warning') : 24;
    this.alert = map.get('alert') ? +map.get('alert') : 12;
    this.orderNumberDateFrom = map.get('order_number_date_from') ? map.get('order_number_date_from') : 'due_date';
    this.menuHideAgreements = map.get('menu_hide_agreements') ? JSON.parse(map.get('menu_hide_agreements')) : false;
    this.menuHideRequests = map.get('menu_hide_requests') ? JSON.parse(map.get('menu_hide_requests')) : false;
    this.menuHideWorkTime = map.get('menu_hide_worktime') ? JSON.parse(map.get('menu_hide_worktime')) : false;
    this.menuHideWarehouses = map.get('menu_hide_warehouses') ? JSON.parse(map.get('menu_hide_warehouses')) : false;
    this.menuHideProducts = map.get('menu_hide_products') ? JSON.parse(map.get('menu_hide_products')) : false;
    this.allowOrderWoDevices = map.get('allow_order_wo_devices') ? JSON.parse(map.get('allow_order_wo_devices')) : false;
    this.dateFormat = map.get('date_format') ? map.get('date_format') : 'dd.MM.yyyy';
    this.timeFormat = map.get('time_format') ? map.get('time_format') : 'HH:mm';
    this.dateTimeFormat = map.get('date_time_format') ? map.get('date_time_format') : 'yyyy.MM.dd HH:mm';
    this.enableUserSchedule = map.get('enable_user_schedule') ? JSON.parse(map.get('enable_user_schedule')) : true;
    this.teamLeadOn = map.get('team_lead_on') ? JSON.parse(map.get('team_lead_on')) : true;
    this.statusChangeWorkOff = map.get('status_change_work_off') ? JSON.parse(map.get('status_change_work_off')) : true;
    this.noteAddWorkOff = map.get('note_add_work_off') ? JSON.parse(map.get('note_add_work_off')) : true;
    this.photoAddWorkOff = map.get('photo_add_work_off') ? JSON.parse(map.get('photo_add_work_off')) : true;
    this.allowPhotoFromGallery = map.get('allow_photo_from_gallery') ? JSON.parse(map.get('allow_photo_from_gallery')) : false;
    this.allowAddingPdfToOrder = map.get('allow_adding_pdf_to_order') ? JSON.parse(map.get('allow_adding_pdf_to_order')) : false;
    this.formAllowSaveAsDraft = map.get('form_allow_save_as_draft') ? JSON.parse(map.get('form_allow_save_as_draft')) : true;
    this.formAllowCopyFromPDF = map.get('form_allow_copy_from_pdf') ? JSON.parse(map.get('form_allow_copy_from_pdf')) : true;
    this.formFieldFontFactor = map.get('form_field_font_factor') ? +map.get('form_field_font_factor') : 1.4;
    this.formTableFontFactor = map.get('form_table_font_factor') ? +map.get('form_table_font_factor') : 1.2;
    this.formTableMaxCol = map.get('form_table_max_col') ? +map.get('form_table_max_col') : 4;
    this.formTableMaxRow = map.get('form_table_max_row') ? +map.get('form_table_max_row') : 4;
    this.formEmailAddress = map.get('form_email_address') ? map.get('form_email_address') : '';
    this.formCopyEmailAddress = map.get('form_copy_email_address') ? map.get('form_copy_email_address') : '';
    this.formEmailTemplateId = map.get('form_email_template_id') ? map.get('form_email_template_id') : '';
    this.warehouseReleaseTableConfigData =  map.get('warehouse_release_table') ?
      new WarehouseReleaseTableConfigData(parseWarehouseReleaseTableConfig( map.get('warehouse_release_table')))
      : null;
    this.warehouseEnableStockOrder = map.get('stock_order')
      ? JSON.parse(map.get('stock_order')) : false;
    this.warehouseAllowNegativeStock = map.get('warehouse_allow_negative_stock')
      ? JSON.parse(map.get('warehouse_allow_negative_stock')) : false;
    this.warehouseAllowAdditionalSrvm = map.get('warehouse_allow_additional_srvm')
      ? JSON.parse(map.get('warehouse_allow_additional_srvm')) : false;
    this.warehouseNotShopZeroStock = map.get('warehouse_not_show_zero_stock')
      ? JSON.parse(map.get('warehouse_not_show_zero_stock')) : true;
    this.warehouseAllowedStatuses = map.get('warehouse_allowed_statuses') ? map.get('warehouse_allowed_statuses').split(',') : [];
    this.branchesEnable = map.get('branches_enable')
      ? JSON.parse(map.get('branches_enable')) : false;
    this.branchesBranchChangeByBranchAdmin = map.get('branches_change_by_branch_admin')
      ? JSON.parse(map.get('branches_change_by_branch_admin')) : false;
    this.branchesRemoveTeamFromOrder = map.get('branches_remove_team_from_order')
      ? JSON.parse(map.get('branches_remove_team_from_order')) : true;
    this.departmentsEnable = map.get('departments_enable')
      ? JSON.parse(map.get('departments_enable')) : false;
    this.sequentialActionsEnable = map.get('sequential_actions_enable')
      ? JSON.parse(map.get('sequential_actions_enable')) : false;
    this.customerAllowDuplicatedVATcode = map.get('customer_allow_duplicated_VAT_code')
      ? JSON.parse(map.get('customer_allow_duplicated_VAT_code')) : false;
    this.enableSupervisorRole = map.get('supervisor_enable')
      ? JSON.parse(map.get('supervisor_enable')) : false;
    this.trackingEnable = map.get('tracking_enable') ? JSON.parse(map.get('tracking_enable')) : false;
    this.trackingInBackground = map.get('tracking_in_background') ? JSON.parse(map.get('tracking_in_background')) : false;
    this.trackingHistoryDays = map.get('tracking_history_days') ? JSON.parse(map.get('tracking_history_days')) : 2;
    this.trackingCalculateDistance = map.get('tracking_calculate_distance') ? JSON.parse(map.get('tracking_calculate_distance')) : false;
    this.trackingDistanceHistoryDays = map.get('tracking_distance_history_days') ? JSON.parse(map.get('tracking_distance_history_days')) : 5;
    this.securityEnableMFA = map.get('security_enable_mfa') ? JSON.parse(map.get('security_enable_mfa')) : false;
    this.securityMFASMS = map.get('security_mfa_sms') ? JSON.parse(map.get('security_mfa_sms')) : false;
    this.securityMFAAuthApp = map.get('security_mfa_auth_app') ? JSON.parse(map.get('security_mfa_auth_app')) : false;
    this.securityMFAPhoneLocalBiometric = map.get('security_mfa_phone_local_biometric') ? JSON.parse(map.get('security_mfa_phone_local_biometric')) : false;
    this.securityLoginLocalBiometric = map.get('security_login_local_biometric') ? JSON.parse(map.get('security_login_local_biometric')) : false;


  }

  toMap(conf: Configuration): Map<string, string> {
    const map = new Map<string, string>();
    map.set('warning', conf.warning.toString());
    map.set('alert', conf.alert.toString());
    map.set('order_number_date_from', conf.orderNumberDateFrom);
    map.set('menu_hide_requests', conf.menuHideRequests.toString());
    map.set('menu_hide_agreements', conf.menuHideAgreements.toString());
    map.set('menu_hide_products', conf.menuHideProducts.toString());
    map.set('menu_hide_worktime', conf.menuHideWorkTime.toString());
    map.set('menu_hide_warehouses', conf.menuHideWarehouses.toString());
    map.set('allow_order_wo_devices', conf.allowOrderWoDevices.toString());
    map.set('date_format', conf.dateFormat);
    map.set('time_format', conf.timeFormat);
    map.set('date_time_format', conf.dateTimeFormat);
    map.set('enable_user_schedule', conf.enableUserSchedule.toString());
    map.set('team_lead_on', conf.teamLeadOn.toString());
    map.set('status_change_work_off', conf.statusChangeWorkOff.toString());
    map.set('note_add_work_off', conf.noteAddWorkOff.toString());
    map.set('photo_add_work_off', conf.photoAddWorkOff.toString());
    map.set('allow_photo_from_gallery', conf.allowPhotoFromGallery.toString());
    map.set('allow_adding_pdf_to_order', conf.allowAddingPdfToOrder.toString());
    map.set('form_allow_save_as_draft', conf.formAllowSaveAsDraft.toString());
    map.set('form_allow_copy_from_pdf', conf.formAllowCopyFromPDF.toString());
    map.set('form_field_font_factor', conf.formFieldFontFactor.toString());
    map.set('form_table_font_factor', conf.formTableFontFactor.toString());
    map.set('form_table_max_col', conf.formTableMaxCol.toString());
    map.set('form_table_max_row', conf.formTableMaxRow.toString());
    map.set('form_email_address', conf.formEmailAddress);
    map.set('form_copy_email_address', conf.formCopyEmailAddress);
    map.set('form_email_template_id', conf.formEmailTemplateId);
    if (conf.warehouseReleaseTableConfigData) {
      map.set('warehouse_release_table', serializeWarehouseReleaseTableConfig(conf.warehouseReleaseTableConfigData.warehouseReleaseTableConfig));

      map.set('stock_order', conf.warehouseEnableStockOrder.toString());
      map.set('warehouse_allow_negative_stock', conf.warehouseAllowNegativeStock.toString());
      map.set('warehouse_allow_additional_srvm', conf.warehouseAllowAdditionalSrvm.toString());
      map.set('warehouse_not_show_zero_stock', conf.warehouseNotShopZeroStock.toString());
      map.set('warehouse_allowed_statuses', conf.warehouseAllowedStatuses.toString());
      map.set('branches_enable', conf.branchesEnable.toString());
      map.set('branches_change_by_branch_admin', conf.branchesBranchChangeByBranchAdmin.toString());
      map.set('branches_remove_team_from_order', conf.branchesRemoveTeamFromOrder.toString());
      map.set('departments_enable', conf.departmentsEnable.toString());
      map.set('sequential_actions_enable', conf.sequentialActionsEnable.toString());
      map.set('customer_allow_duplicated_VAT_code', conf.customerAllowDuplicatedVATcode.toString());
      map.set('supervisor_enable', conf.enableSupervisorRole.toString());
      map.set('tracking_enable', conf.trackingEnable.toString());
      map.set('tracking_in_background', conf.trackingInBackground.toString());
      map.set('tracking_history_days', conf.trackingHistoryDays.toString());
      map.set('tracking_calculate_distance', conf.trackingCalculateDistance.toString());
      map.set('tracking_distance_history_days', conf.trackingDistanceHistoryDays.toString());
      map.set('security_enable_mfa', conf.securityEnableMFA.toString());
      map.set('security_mfa_sms', conf.securityMFASMS.toString());
      map.set('security_mfa_auth_app', conf.securityMFAAuthApp.toString());
      map.set('security_mfa_phone_local_biometric', conf.securityMFAPhoneLocalBiometric.toString());
      map.set('security_login_local_biometric', conf.securityLoginLocalBiometric.toString());
      return map;
    }
  }
}
